import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Anchor, Eye, ShieldCheck, Check, Ban, Clock, Stamp, Sparkles, Sliders,
  FileSignature, Activity, Container, Wind, AlertTriangle, ArrowRight, Printer,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AboutDialog from "@/components/AboutDialog";

const STATUS_META = {
  ALLOWED:   { chip: "fc-chip-allowed",   icon: Check,  label: "Allowed",   tone: "text-[var(--green)]" },
  BLOCKED:   { chip: "fc-chip-blocked",   icon: Ban,    label: "Blocked",   tone: "text-[var(--red)]" },
  ESCALATED: { chip: "fc-chip-escalated", icon: Clock,  label: "Escalated", tone: "text-[var(--amber)]" },
  APPROVED:  { chip: "fc-chip-approved",  icon: Stamp,  label: "Approved",  tone: "text-[#1E40AF]" },
};

const NOTICE = [
  "A real freight disruption affects several parties.",
  "Recommendations alone are not enough.",
  "The governance gate checks who may act.",
  "Actions are allowed, blocked or escalated.",
  "Every consequential action generates attributable evidence.",
];

export default function SnapshotView() {
  const { token } = useParams();
  const [snap, setSnap] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.getSnapshot(token)
      .then(setSnap)
      .catch(e => setError(e?.response?.status === 410
        ? "This snapshot has expired."
        : "Snapshot not found."));
  }, [token]);

  if (error) {
    return (
      <div className="max-w-[800px] mx-auto px-6 py-20 text-center">
        <AlertTriangle size={28} className="mx-auto mb-4 text-[var(--ink-soft)]"/>
        <h1 className="display text-[28px] mb-2">{error}</h1>
        <p className="text-[var(--ink-soft)]">Ask the host to share a fresh assessor link.</p>
      </div>
    );
  }
  if (!snap) return <div className="max-w-[1200px] mx-auto px-6 py-12 text-[var(--ink-soft)]">Loading snapshot…</div>;

  const s = snap.scenario;
  const sift = snap.sifty_signals;
  const rwi = snap.rwi_runs;
  const counts = snap.summary?.outcomes || {};

  const downloadPdf = () => {
    window.open(`/snapshot/${token}/print`, "_blank");
  };
  const copyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    toast.success("Assessor link copied to clipboard");
  };

  return (
    <div className="bg-[var(--paper)] min-h-screen pb-16 fc-fade-up" data-testid="snapshot-root">
      {/* Host note (optional) — sits above the read-only banner, modest cream tint */}
      {snap.note_lines && snap.note_lines.length > 0 && (
        <div className="bg-[#FAF6EA] border-b border-[#E8DDB8]" data-testid="host-note">
          <div className="max-w-[1200px] mx-auto px-6 py-4 flex gap-5 items-start">
            <div className="fc-eyebrow shrink-0 pt-0.5" style={{ color: "#7C5E10" }}>Note from host</div>
            <div className="border-l-2 border-[#C9A227] pl-4 space-y-1 min-w-0">
              {snap.note_lines.map((line, i) => (
                <div
                  key={i}
                  data-testid={`host-note-line-${i + 1}`}
                  className={`leading-snug text-[var(--ink)] ${i === 0 ? "text-[14px] font-medium" : "text-[13px] text-[#3F3722]"}`}
                >
                  {line}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Read-only banner */}
      <div className="bg-[var(--navy)] text-white">
        <div className="max-w-[1200px] mx-auto px-6 py-2 flex flex-wrap items-center gap-3 text-[12px]">
          <Eye size={13}/>
          <span className="mono uppercase tracking-[0.16em]">Read-only assessor snapshot</span>
          <span className="opacity-50">·</span>
          <span className="mono opacity-80">{snap.token}</span>
          <span className="opacity-50">·</span>
          <span className="opacity-80">Captured {new Date(snap.created_at).toUTCString()}</span>
          {snap.expires_at && (
            <><span className="opacity-50">·</span>
              <span className="opacity-80">Expires {new Date(snap.expires_at).toLocaleDateString()}</span></>
          )}
          <div className="ml-auto flex gap-3 items-center">
            <AboutDialog variant="light" testid="snapshot-about-trigger"/>
            <Button onClick={copyLink} size="sm" variant="ghost" className="text-white hover:bg-white/10 h-7 text-[11px]" data-testid="snapshot-copy-link">
              Copy link
            </Button>
            <Button onClick={downloadPdf} size="sm" variant="ghost" className="text-white hover:bg-white/10 h-7 text-[11px]" data-testid="snapshot-download-pdf">
              <Printer size={11} className="mr-1.5"/> PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 pt-10">
        {/* Hero */}
        <header className="grid lg:grid-cols-12 gap-8 items-end pb-10 border-b border-[var(--line)]">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-2 mb-4">
              <Anchor size={16} className="text-[var(--navy)]"/>
              <span className="fc-eyebrow">FreightCierge · Assessor Mode</span>
            </div>
            <h1 className="display text-[40px] md:text-[52px] leading-[0.98] mb-4">
              {s.title}
            </h1>
            <p className="text-[15px] text-[var(--ink)] max-w-[60ch] leading-relaxed">
              A storm has narrowed the tidal window into <strong>{s.terminal.name}</strong>.
              <strong> {s.vessel.name}</strong> reports an ETA slip of <strong>{s.vessel.delay_hours}h</strong>.
              The yard is at <strong>{s.terminal.yard_capacity_pct}%</strong> capacity. {snap.summary?.at_risk_containers} of {s.containers.length} containers
              are at risk — including a regulated pharma cut-off. Multiple roles must act.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Button asChild className="bg-[var(--navy)] hover:bg-black rounded-sm h-11" data-testid="snapshot-walkthrough-btn">
                <Link to="/walkthrough">Start 3-minute walkthrough <ArrowRight size={14} className="ml-2"/></Link>
              </Button>
              <Button onClick={downloadPdf} variant="outline" className="rounded-sm h-11" data-testid="snapshot-pdf-btn">
                <Printer size={14} className="mr-2"/> Download PDF evidence pack
              </Button>
            </div>
          </div>

          {/* What to notice */}
          <aside className="lg:col-span-4 fc-card p-6" data-testid="what-to-notice">
            <Info size={16} className="text-[var(--teal)] mb-3" strokeWidth={1.6}/>
            <div className="fc-eyebrow mb-3">What to notice</div>
            <ul className="text-[13px] space-y-2 leading-relaxed">
              {NOTICE.map((n, i) => (
                <li key={i} className="flex gap-2"><span className="text-[var(--teal)]">·</span>{n}</li>
              ))}
            </ul>
          </aside>
        </header>

        {/* Outcome summary */}
        <section className="py-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--line)] border border-[var(--line)] mb-2" data-testid="snapshot-outcome-summary">
          {[
            { k: "ALLOWED",   label: "Allowed",   value: counts.ALLOWED   || 0 },
            { k: "BLOCKED",   label: "Blocked",   value: counts.BLOCKED   || 0 },
            { k: "ESCALATED", label: "Escalated", value: counts.ESCALATED || 0 },
            { k: "APPROVED",  label: "Approved",  value: counts.APPROVED  || 0 },
          ].map(o => {
            const m = STATUS_META[o.k];
            const Icon = m.icon;
            return (
              <div key={o.k} className="bg-white p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Icon size={14} className={m.tone}/>
                  <span className="fc-eyebrow">{o.label}</span>
                </div>
                <div className={`fc-stat text-[42px] leading-none ${m.tone}`}>{o.value}</div>
                <div className="text-[11px] text-[var(--ink-soft)] mt-1">receipts</div>
              </div>
            );
          })}
        </section>

        {/* Sections */}
        <section className="py-12 border-t border-[var(--line)]">
          <SectionTitle eyebrow="01" title="Scenario" sub="What disruption occurred · what was at risk · which roles were involved"/>
          <div className="grid md:grid-cols-2 gap-px bg-[var(--line)] border border-[var(--line)]">
            <Block icon={Wind} label="Weather" value={s.vessel.weather}/>
            <Block icon={AlertTriangle} label="ETA delay" value={`+${s.vessel.delay_hours}h`} red/>
            <Block icon={Container} label="Yard utilisation" value={`${s.terminal.yard_capacity_pct}%`} red/>
            <Block icon={Activity} label="At-risk containers" value={`${snap.summary?.at_risk_containers || 0} of ${s.containers.length}`}/>
          </div>
          <div className="mt-6">
            <div className="fc-eyebrow mb-3">Roles in play</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--line)] border border-[var(--line)]">
              {snap.roles.map(r => (
                <div key={r.id} className="bg-white p-4">
                  <div className="display text-[14px]">{r.label}</div>
                  <div className="text-[11px] text-[var(--ink-soft)] mt-0.5">{r.org}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 border-t border-[var(--line)]">
          <SectionTitle eyebrow="02" title="Recommended actions" sub="Surfaced by the signal layer · governed by KATLAS"/>
          <div className="space-y-2">
            {s.actions.map(a => {
              const m = STATUS_META[a.outcome];
              const Icon = m.icon;
              const role = snap.roles.find(r => r.id === a.permitted_role);
              return (
                <div key={a.id} className="border border-[var(--line)] p-4 bg-white" data-testid={`snap-action-${a.id}`}>
                  <div className="flex flex-wrap items-start gap-3 justify-between">
                    <div className="min-w-0">
                      <div className="display text-[15px]">{a.label}</div>
                      <div className="text-[12px] text-[var(--ink-soft)] mt-1">{a.summary}</div>
                      <div className="mono text-[10px] uppercase tracking-wider text-[var(--ink-soft)] mt-2">
                        Recommended by · {a.recommended_by}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className={`fc-chip ${m.chip}`}><Icon size={10}/> {m.label}</span>
                      <div className="text-[11px] text-[var(--ink-soft)] mt-2">→ {role?.label}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {snap.enabled?.sifty && sift && (
            <div className="mt-6 fc-card p-5" data-testid="snap-sifty">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={14} className="text-[var(--teal)]"/>
                <span className="fc-eyebrow">SiftyML predictive signals · supporting input only</span>
              </div>
              <p className="text-[14px] mb-3">{sift.summary}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {sift.scores.map((sc, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-[12px] mb-1">
                      <span className="text-[var(--ink-soft)]">{sc.label}</span>
                      <span className="mono">{sc.value}</span>
                    </div>
                    <div className="h-1 bg-[var(--paper)] border border-[var(--line)]">
                      <div className="h-full bg-[var(--navy)]" style={{ width: `${sc.value}%` }}/>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-[var(--ink-soft)] pt-3 mt-3 border-t border-[var(--line)]">
                AI may recommend. It does not determine authority.
              </div>
            </div>
          )}
        </section>

        <section className="py-12 border-t border-[var(--line)]">
          <SectionTitle eyebrow="03" title="Governance gate" sub="The KATLAS node applies CAR (Custody, Authority, Receipts) at runtime"/>
          <div className="grid sm:grid-cols-3 gap-px bg-[var(--line)] border border-[var(--line)]">
            {[
              { t: "Custody",   b: "Who currently holds responsibility or control over this asset." },
              { t: "Authority", b: "Who is permitted to decide or act under these conditions." },
              { t: "Receipts",  b: "What signed evidence the action will produce." },
            ].map(c => (
              <div key={c.t} className="bg-white p-5">
                <div className="mono text-[11px] uppercase tracking-wider text-[var(--ink-soft)] mb-1">{c.t}</div>
                <p className="text-[13px]">{c.b}</p>
              </div>
            ))}
          </div>
          <details className="mt-5" data-testid="snap-katlas-detail">
            <summary className="text-[12px] text-[var(--ink-soft)] cursor-pointer fc-link-underline inline-block">
              Technical detail · how the KATLAS node evaluates an action
            </summary>
            <div className="mt-3 fc-card p-4 text-[12px] text-[var(--ink)] leading-relaxed">
              At evaluation time the KATLAS node performs an identity and credential check, a role and
              organisation match, a context check on the workflow session, and a decision-rights and
              policy evaluation before the action is allowed, blocked or escalated. A signed receipt is
              produced for every consequential outcome.
            </div>
          </details>
        </section>

        <section className="py-12 border-t border-[var(--line)]">
          <SectionTitle eyebrow="04" title="Decision outcome" sub={`${snap.receipts.length} attributable receipts captured`}/>
          {snap.receipts.length === 0 ? (
            <div className="fc-card p-8 text-[var(--ink-soft)] text-sm text-center">
              No actions were run before this snapshot was captured.
            </div>
          ) : (
            <div className="border border-[var(--line)] divide-y divide-[var(--line)] bg-white" data-testid="snap-receipts">
              {snap.receipts.map(r => {
                const m = STATUS_META[r.status] || STATUS_META.ALLOWED;
                const Icon = m.icon;
                return (
                  <div key={r.receipt_id} className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`fc-chip ${m.chip}`}><Icon size={10}/> {m.label}</span>
                      <span className="mono text-[10px] text-[var(--ink-soft)] ml-auto">{r.receipt_id}</span>
                    </div>
                    <div className="display text-[14px]">{r.action_label}</div>
                    <div className="text-[12px] mt-1">{r.actor_role} · {r.actor_org}</div>
                    <div className="text-[12px] text-[var(--ink-soft)] mt-1">{r.decision_basis}</div>
                    <div className="mono text-[11px] text-[var(--ink-soft)] mt-1">{new Date(r.timestamp).toUTCString()}</div>
                    {r.approvals?.length > 0 && (
                      <div className="text-[11px] mt-2 pt-2 border-t border-[var(--line)] text-[var(--ink-soft)]">
                        Co-signed by {r.approvals[0].approver_role}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="py-12 border-t border-[var(--line)]">
          <SectionTitle eyebrow="05" title="Receipts" sub="Signed evidence · attributable to a role and an action"/>
          <div className="grid sm:grid-cols-3 gap-px bg-[var(--line)] border border-[var(--line)]">
            {[
              { t: "Actor + role + organisation", b: "Every receipt names the person, role, and organisation that triggered the outcome." },
              { t: "Authority basis",             b: "The runtime authority used to allow, block or escalate the action — and any co-sign." },
              { t: "Linked event + downstream",   b: "Each receipt references the upstream event and lists downstream notifications." },
            ].map(c => (
              <div key={c.t} className="bg-white p-5">
                <FileSignature size={16} className="text-[var(--navy)] mb-3" strokeWidth={1.6}/>
                <div className="display text-[14px] mb-1">{c.t}</div>
                <p className="text-[12px] text-[var(--ink-soft)]">{c.b}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="py-12 border-t border-[var(--line)]">
          <SectionTitle eyebrow="06" title="Evidence pack" sub="A printable assurance artefact for regulators, customers and internal audit"/>
          <div className="fc-card p-7 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="display text-[18px] mb-1">FreightCierge Evidence Pack</div>
              <div className="text-[13px] text-[var(--ink-soft)]">Cover · scenario · governance decisions · receipts · evidence summary · business outcome</div>
            </div>
            <Button onClick={downloadPdf} className="bg-[var(--navy)] hover:bg-black rounded-sm h-10" data-testid="snap-evidence-pdf-btn">
              <Printer size={14} className="mr-2"/> Download PDF
            </Button>
          </div>
        </section>

        {snap.enabled?.rwi && rwi && (
          <section className="py-12 border-t border-[var(--line)]">
            <SectionTitle eyebrow="07" title="Scenario stress" sub="RWI comparison · unguided vs governed response"/>
            <div className="border border-[var(--line)] bg-white p-5" data-testid="snap-rwi">
              <div className="grid grid-cols-3 gap-3 mb-3 mono text-[11px] uppercase tracking-wider text-[var(--ink-soft)]">
                <div></div><div>Unguided</div><div>Governed</div>
              </div>
              {[
                ["Resolution time",       `${rwi.unguided.resolution_minutes}m`,         `${rwi.governed.resolution_minutes}m`],
                ["Unauthorised actions",  rwi.unguided.unauthorised_actions,             rwi.governed.unauthorised_actions],
                ["Decision confidence",   `${rwi.unguided.decision_confidence}%`,        `${rwi.governed.decision_confidence}%`],
                ["Evidence completeness", `${rwi.unguided.evidence_completeness}%`,      `${rwi.governed.evidence_completeness}%`],
              ].map(row => (
                <div key={row[0]} className="grid grid-cols-3 gap-3 py-2 border-t border-[var(--line)] text-[13px]">
                  <div>{row[0]}</div>
                  <div className="mono text-[var(--ink-soft)]">{row[1]}</div>
                  <div className="mono text-[var(--green)] font-semibold">{row[2]}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="py-12 border-t border-[var(--line)]">
          <SectionTitle eyebrow="08" title="Business outcome" sub="What changes for assessors and operators"/>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--line)] border border-[var(--line)]">
            {[
              ["Reduced ambiguity", "The right role sees the right information at the right moment."],
              ["Clearer accountability", "Every consequential action is attributed to a named role."],
              ["Faster exception resolution", "Recommendations are routed to the role that may decide."],
              ["Stronger audit readiness", "An evidence pack is assembled as the response unfolds."],
            ].map(o => (
              <div key={o[0]} className="bg-white p-5">
                <ShieldCheck size={16} className="text-[var(--teal)] mb-3" strokeWidth={1.6}/>
                <div className="display text-[14px] mb-1">{o[0]}</div>
                <p className="text-[12px] text-[var(--ink-soft)]">{o[1]}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function SectionTitle({ eyebrow, title, sub }) {
  return (
    <div className="mb-6">
      <div className="fc-eyebrow mb-2">{eyebrow} · {title.toUpperCase()}</div>
      <h2 className="display text-[28px] leading-tight mb-1">{title}</h2>
      <p className="text-[var(--ink-soft)] text-[14px]">{sub}</p>
    </div>
  );
}

function Block({ icon: Icon, label, value, red }) {
  return (
    <div className="bg-white p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className="text-[var(--ink-soft)]"/>
        <span className="fc-eyebrow">{label}</span>
      </div>
      <div className={`display text-[20px] leading-tight ${red ? "text-[var(--red)]" : ""}`}>{value}</div>
    </div>
  );
}
